/**
 * Вход по ссылке из телеграма.
 *
 * Бот присылает кнопку, она ведёт сюда с одноразовым токеном. Страница
 * обменивает его на сессию и уводит в инбокс — человек видит её долю секунды,
 * если всё хорошо, и внятный текст, если нет.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/ui/AuthLayout';
import { Spinner } from '../components/ui/Spinner';
import { useTelegramExchange } from '../api/telegram-auth';

type Stage = 'working' | 'failed';

export function TelegramLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { mutateAsync: exchange } = useTelegramExchange();

  const [stage, setStage] = useState<Stage>('working');

  // Токен приходит во фрагменте, а не в параметрах запроса: фрагмент
  // не отправляется на сервер, поэтому не оседает в логах прокси и не
  // утекает через Referer на сторонние ресурсы
  const [token] = useState<string | null>(() => {
    const raw = window.location.hash.replace(/^#/, '');
    return new URLSearchParams(raw).get('token');
  });

  // Обмен строго один раз: токен одноразовый, второй заход получил бы отказ
  // на собственную же успешную попытку
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Адресную строку чистим сразу, до любых сетевых запросов: иначе ссылка
    // с действующим токеном осядет в истории браузера
    window.history.replaceState(null, '', window.location.pathname);

    if (!token) {
      setStage('failed');
      return;
    }

    exchange({ token })
      .then(() => navigate('/inbox', { replace: true }))
      .catch(() => setStage('failed'));
  }, [token, exchange, navigate]);

  return (
    <AuthLayout subtitle="Operator Console">
      {stage === 'working' ? (
        <div className="flex items-center gap-3 text-text-secondary">
          <Spinner size="sm" />
          <span className="text-sm">Проверяем ссылку…</span>
        </div>
      ) : (
        <div className="space-y-3">
          <h2 className="font-heading text-xl font-semibold text-text-primary">
            Ссылка больше не действует
          </h2>
          <p className="text-sm text-text-secondary">
            Ссылки живут пять минут и срабатывают один раз — так они бесполезны
            для того, кто увидит их позже.
          </p>
          <p className="text-sm text-text-secondary">
            Напишите боту <span className="text-text-primary">/login</span>,
            и он пришлёт новую.
          </p>
        </div>
      )}
    </AuthLayout>
  );
}
